import { format, parseISO } from "date-fns";
import type { IntlShape } from "react-intl";
import { Role } from "@/core/components/auth/auth.types";
import { RestrictionTypeTranslation } from "@/core/types/utils";
import type { Restriction, RestrictionType } from "./restriction.types";

/**
 * Value used by the rate/room filters for restrictions that apply to every rate or
 * every room (all the related ids are null). Distinct from "no filter".
 */
export const ALL_VALUE = "__all__";

/** Rows written from admin; every other origin is a channel (SiteMinder, Infor, Mews…). */
export const MANUAL_ORIGIN = "manual";

export const RESTRICTION_ORIGIN_FILTERS = ["manual", "channel"] as const;
export type RestrictionOriginFilter = (typeof RESTRICTION_ORIGIN_FILTERS)[number];

export const isManualRestriction = (restriction: Restriction) =>
	restriction.origin === MANUAL_ORIGIN;

// Same list the API enforces on the write routes (spec Q3).
export const RESTRICTION_WRITE_ROLES: string[] = [
	Role.Internal,
	Role.Reseller,
	Role.Organizator,
	Role.PropertyManager,
];

export type RestrictionFilters = {
	search: string;
	fromDate?: string;
	toDate?: string;
	type?: RestrictionType;
	rate?: string;
	room?: string;
	origin?: RestrictionOriginFilter;
};

export const EMPTY_FILTERS: RestrictionFilters = {
	search: "",
	fromDate: undefined,
	toDate: undefined,
	type: undefined,
	rate: undefined,
	room: undefined,
	origin: undefined,
};

export type FacetOption = { value: string; label: string };

export type RestrictionFacets = {
	rates: FacetOption[];
	rooms: FacetOption[];
};

const toDay = (iso: string) => format(parseISO(iso), "yyyy-MM-dd");

const appliesToAllRates = (restriction: Restriction) =>
	restriction.rateId === null &&
	restriction.baseRateId === null &&
	restriction.rateGroupId === null;

const appliesToAllRooms = (restriction: Restriction) =>
	restriction.roomId === null && restriction.accommodationType === null;

/**
 * The value the rate filter matches against, mirroring what the Rate column renders:
 * the expanded rate / rate group / base rate when present, ALL_VALUE when the
 * restriction applies to every rate. A restriction carrying an id the API did not
 * expand still matches by raw id — it just isn't offered as a filter option.
 */
const rateValueOf = (restriction: Restriction): string => {
	if (restriction.rate) return restriction.rate.id;
	if (restriction.rateGroup) return restriction.rateGroup.id;
	if (restriction.baseRate) return restriction.baseRate.id;
	if (appliesToAllRates(restriction)) return ALL_VALUE;
	return (
		restriction.rateId ??
		restriction.baseRateId ??
		restriction.rateGroupId ??
		""
	);
};

const rateLabelOf = (restriction: Restriction): string | null => {
	if (restriction.rate)
		return `${restriction.rate.rateCode} - ${restriction.rate.shortTitle}`;
	if (restriction.rateGroup)
		return `${restriction.rateGroup.rateGroupCode} - ${restriction.rateGroup.name}`;
	if (restriction.baseRate)
		return `${restriction.baseRate.rateCode} - ${restriction.baseRate.shortTitle}`;
	return null;
};

const roomValueOf = (restriction: Restriction): string => {
	if (restriction.room) return restriction.room.id;
	if (appliesToAllRooms(restriction)) return ALL_VALUE;
	return restriction.roomId ?? "";
};

const roomLabelOf = (restriction: Restriction): string | null =>
	restriction.room
		? `${restriction.room.roomCode} - ${restriction.room.shortTitle}`
		: null;

/** Sorted by label, with the "applies to all" option pinned first. */
const toOptions = (labels: Map<string, string>): FacetOption[] =>
	[...labels.entries()]
		.map(([value, label]) => ({ value, label }))
		.sort((a, b) => {
			if (a.value === ALL_VALUE) return -1;
			if (b.value === ALL_VALUE) return 1;
			return a.label.localeCompare(b.label);
		});

/**
 * Rate and room options are derived from the loaded restrictions, so the dropdowns
 * never offer a filter that would return zero rows.
 */
export const getRestrictionFacets = (
	restrictions: Restriction[],
	intl: IntlShape,
): RestrictionFacets => {
	const rates = new Map<string, string>();
	const rooms = new Map<string, string>();

	for (const restriction of restrictions) {
		const rateLabel = rateLabelOf(restriction);
		if (rateLabel) {
			rates.set(rateValueOf(restriction), rateLabel);
		} else if (appliesToAllRates(restriction)) {
			rates.set(
				ALL_VALUE,
				intl.formatMessage({ id: "restrictions.appliesToAllRates" }),
			);
		}

		const roomLabel = roomLabelOf(restriction);
		if (roomLabel) {
			rooms.set(roomValueOf(restriction), roomLabel);
		} else if (appliesToAllRooms(restriction)) {
			rooms.set(
				ALL_VALUE,
				intl.formatMessage({ id: "restrictions.appliesToAllRooms" }),
			);
		}
	}

	return { rates: toOptions(rates), rooms: toOptions(rooms) };
};

/** Everything the row renders, so the search box matches what the admin sees. */
const searchHaystack = (restriction: Restriction, intl: IntlShape): string => {
	const parts: (string | number | null)[] = [
		format(parseISO(restriction.startDate), "MM/dd/yyyy"),
		format(parseISO(restriction.endDate), "MM/dd/yyyy"),
		intl.formatMessage({ id: RestrictionTypeTranslation[restriction.type] }),
		restriction.days?.join(" ") ?? null,
		restriction.days?.length === 7
			? intl.formatMessage({ id: "common.allDays2" })
			: null,
		rateLabelOf(restriction) ??
			(appliesToAllRates(restriction)
				? intl.formatMessage({ id: "restrictions.allRates" })
				: null),
		roomLabelOf(restriction) ??
			(appliesToAllRooms(restriction)
				? intl.formatMessage({ id: "restrictions.allRooms" })
				: null),
		restriction.minLeadIn,
		restriction.maxLeadIn,
		restriction.minLOS,
		restriction.maxLOS,
		restriction.minPrice,
		restriction.maxPrice,
	];

	return parts
		.filter((part) => part !== null)
		.join(" ")
		.toLowerCase();
};

/**
 * A restriction matches when its own range falls *inside* the selected one: picking
 * June returns the restrictions of June, not every long-running restriction that
 * happens to cross it. Each end is optional and constrains only its own side —
 * "from" alone means "starts on or after", "to" alone means "ends on or before".
 */
const matchesDateRange = (
	restriction: Restriction,
	from?: string,
	to?: string,
) => {
	if (from && toDay(restriction.startDate) < from) return false;
	if (to && toDay(restriction.endDate) > to) return false;
	return true;
};

export const filterRestrictions = (
	restrictions: Restriction[],
	filters: RestrictionFilters,
	intl: IntlShape,
): Restriction[] => {
	const search = filters.search.trim().toLowerCase();

	return restrictions.filter((restriction) => {
		if (filters.type && restriction.type !== filters.type) return false;
		if (filters.rate && rateValueOf(restriction) !== filters.rate) return false;
		if (filters.room && roomValueOf(restriction) !== filters.room) return false;
		if (
			filters.origin &&
			(filters.origin === "manual") !== isManualRestriction(restriction)
		)
			return false;
		if (!matchesDateRange(restriction, filters.fromDate, filters.toDate))
			return false;
		if (search && !searchHaystack(restriction, intl).includes(search))
			return false;
		return true;
	});
};

/**
 * Drives the badge on the Search button. The free-text search counts too: it
 * lives inside the dialog, so the badge is the only hint that it is narrowing
 * the list.
 */
export const countActiveFilters = (filters: RestrictionFilters): number =>
	[
		filters.search.trim(),
		filters.fromDate || filters.toDate,
		filters.type,
		filters.rate,
		filters.room,
		filters.origin,
	].filter(Boolean).length;
